import { Bracket, FullLayoutOptions, PositionedBracket, RegionLayoutResult, Round, Seed } from "core/models/Bracket"


export function computeRegionLayout(
  rounds: Round[],
  region: 'upper' | 'lower',
  opts: {
    startX: number
    roundSpacing: number
    bracketHeight: number
    leafGap: number
  }
): RegionLayoutResult {
  const { startX, roundSpacing, bracketHeight, leafGap } = opts
  if (!rounds.length) return { positioned: [], height: 0, region }

  interface NodeInfo {
    y: number
  }
  const info: Record<string, NodeInfo> = {}
  const childrenMap = new Map<string, string[]>()
  const seedMap = new Map<string, Seed>()

  for (const r of rounds) {
    for (const s of r.seeds) {
      seedMap.set(s.id, s)
      for (const d of s.dependencies || []) {
        if (!childrenMap.has(d.sourceBracketId))
          childrenMap.set(d.sourceBracketId, [])
        childrenMap.get(d.sourceBracketId)!.push(s.id)
      }
    }
  }

  const anchorIndex =
    rounds.length > 1 && rounds[0].seeds.length >= rounds[1].seeds.length
      ? 0
      : 1
  const anchorSeeds = rounds[anchorIndex].seeds
  for (let i = 0; i < anchorSeeds.length; i++) {
    const s = anchorSeeds[i]
    info[s.id] = { y: i * (bracketHeight + leafGap) }
  }

  for (let ri = anchorIndex + 1; ri < rounds.length; ri++) {
    const round = rounds[ri]
    for (let localIdx = 0; localIdx < round.seeds.length; localIdx++) {
      const seed = round.seeds[localIdx]
      if (info[seed.id]) continue
      const depYs = (seed.dependencies || [])
        .map((d) => info[d.sourceBracketId]?.y)
        .filter((y): y is number => y !== undefined)
      if (depYs.length === 0) {
        info[seed.id] = { y: localIdx * (bracketHeight + leafGap) }
      } else if (depYs.length === 1) {
        info[seed.id] = { y: depYs[0] }
      } else {
        const centers = (seed.dependencies || [])
          .map((d) => info[d.sourceBracketId])
          .filter(Boolean)
          .map((n) => n.y + bracketHeight / 2)
        const parentCenter = (Math.min(...centers) + Math.max(...centers)) / 2
        info[seed.id] = { y: parentCenter - bracketHeight / 2 }
      }
    }
  }

  for (let ri = anchorIndex - 1; ri >= 0; ri--) {
    const round = rounds[ri]
    for (const seed of round.seeds) {
      if (info[seed.id]) continue
      const parents = childrenMap.get(seed.id) || []
      if (!parents.length) continue
      const parentInfos = parents.map((pid) => info[pid]).filter(Boolean)
      if (!parentInfos.length) continue

      if (parentInfos.length === 1) {
        const parentId = parents[0]
        const parentSeed = seedMap.get(parentId)
        if (!parentSeed) continue
        const parentTop = info[parentId].y
        const parentCenter = parentTop + bracketHeight / 2
        const siblingDeps = (parentSeed.dependencies || []).filter((d) =>
          rounds[ri].seeds.some((s) => s.id === d.sourceBracketId)
        )
        const total = siblingDeps.length || 1
        const slot =
          siblingDeps.find((d) => d.sourceBracketId === seed.id)?.teamSlot ?? 0
        if (total === 1) {
          info[seed.id] = { y: parentCenter - bracketHeight / 2 }
        } else {
          const sep = bracketHeight + leafGap
          const childCenter = parentCenter + (slot - (total - 1) / 2) * sep
          info[seed.id] = { y: childCenter - bracketHeight / 2 }
        }
      } else {
        const centers = parentInfos.map((p) => p.y + bracketHeight / 2)
        const c = (Math.min(...centers) + Math.max(...centers)) / 2
        info[seed.id] = { y: c - bracketHeight / 2 }
      }
    }
    let cursor = 0
    for (const s of round.seeds) {
      if (!info[s.id]) {
        info[s.id] = { y: cursor }
        cursor += bracketHeight + leafGap
      }
    }
  }

  for (let ri = 0; ri < rounds.length - 1; ri++) {
    const a = rounds[ri]
    const b = rounds[ri + 1]
    if (a.seeds.length === b.seeds.length && a.seeds.length > 0) {
      const ok = b.seeds.every(
        (s) =>
          s.dependencies.length === 1 &&
          a.seeds.some((as) => as.id === s.dependencies[0].sourceBracketId)
      )
      if (ok) {
        for (const s of b.seeds) {
          const pid = s.dependencies[0].sourceBracketId
          if (info[pid]) info[s.id].y = info[pid].y
        }
      }
    }
  }

  for (const r of rounds) {
    const ordered = r.seeds.slice().sort((x, y) => info[x.id].y - info[y.id].y)
    for (let i = 1; i < ordered.length; i++) {
      const prev = ordered[i - 1]
      const cur = ordered[i]
      const need = info[prev.id].y + bracketHeight + leafGap
      if (info[cur.id].y < need) info[cur.id].y = need
    }
  }

  const positioned: PositionedBracket[] = []
  for (let ri = 0; ri < rounds.length; ri++) {
    const r = rounds[ri]
    const x = startX + ri * roundSpacing
    for (const s of r.seeds) {
      positioned.push({
        id: s.id,
        roundIndex: ri,
        x,
        y: info[s.id].y,
        seed: s,
        region,
      })
    }
  }

  const minY = Math.min(...positioned.map((p) => p.y))
  for (const p of positioned) {
    p.y -= minY
  }
  const height =
    Math.max(...positioned.map((p) => p.y)) + bracketHeight + leafGap

  return { positioned, height, region }
}

export function computeFullLayout(
  data: Bracket,
  options: FullLayoutOptions = {}
) {
  const roundSpacing = options.bracketWidth ? options.bracketWidth + 60 : 260
  const bracketWidth = options.bracketWidth ?? 200
  const bracketHeight = options.bracketHeight ?? 70
  const leafGap = options.leafGap ?? 20
  const regionGap = options.regionGap ?? 160
  const startX = options.startX ?? 40
  const thirdPlaceGap = options.thirdPlaceGap ?? 80

  const isSingle =
    data.mode === 'single' || !data.lower || data.lower.length === 0

  const upperLayout = computeRegionLayout(data.upper, 'upper', {
    startX,
    roundSpacing,
    bracketHeight,
    leafGap,
  })

  const positioned: PositionedBracket[] = [...upperLayout.positioned]
  let totalHeight = upperLayout.height
  let lowerLayout: RegionLayoutResult | null = null

  if (!isSingle) {
    lowerLayout = computeRegionLayout(data.lower || [], 'lower', {
      startX,
      roundSpacing,
      bracketHeight,
      leafGap,
    })
    for (const p of lowerLayout.positioned) {
      p.y += upperLayout.height + regionGap
    }
    positioned.push(...lowerLayout.positioned)
    totalHeight = upperLayout.height + regionGap + lowerLayout.height
  }

  const finals: PositionedBracket[] = []
  const finalRounds = data.final || []

  function findPlaced(id: string): PositionedBracket | undefined {
    return (
      finals.find((p) => p.id === id) || positioned.find((p) => p.id === id)
    )
  }

  if (finalRounds.length) {
    const maxRoundCount = isSingle
      ? data.upper.length
      : Math.max(data.upper.length, (data.lower || []).length)

    const baseX = startX + maxRoundCount * roundSpacing

    if (finalRounds.length === 1 && finalRounds[0].seeds.length === 2) {
      const [seed1, seed2] = finalRounds[0].seeds

      const getY = (seed: Seed) => {
        const deps = seed.dependencies || []
        const depYs = deps
          .map((d) => findPlaced(d.sourceBracketId)?.y)
          .filter((y): y is number => y !== undefined)
        if (depYs.length === 0) {
          return (totalHeight - bracketHeight) / 2
        } else if (depYs.length === 1) {
          return depYs[0]
        } else {
          const centers = deps
            .map((d) => findPlaced(d.sourceBracketId)?.y)
            .filter((v): v is number => v !== undefined)
          if (centers.length) {
            const minY = Math.min(...centers)
            const maxY = Math.max(...centers)
            const midY = (minY + maxY) / 2
            return midY - bracketHeight / 2
          } else {
            return depYs[0]
          }
        }
      }

      const y1 = getY(seed1)

      finals.push(
        {
          id: seed1.id,
          roundIndex: maxRoundCount,
          x: baseX,
          y: y1,
          seed: seed1,
          region: 'final',
        },
        {
          id: seed2.id,
          roundIndex: maxRoundCount,
          x: baseX + bracketWidth + 40,
          y: y1,
          seed: seed2,
          region: 'final',
        }
      )
    } else {
      for (let fi = 0; fi < finalRounds.length; fi++) {
        const fr = finalRounds[fi]
        for (const seed of fr.seeds) {
          const deps = seed.dependencies || []
          const depYs = deps
            .map((d) => findPlaced(d.sourceBracketId)?.y)
            .filter((y): y is number => y !== undefined)

          let y: number
          if (depYs.length === 0) {
            if (isSingle) {
              y = fi * (bracketHeight + leafGap)
            } else {
              const existingRoundSeeds = fr.seeds
                .map((s) => findPlaced(s.id)?.y)
                .filter((y): y is number => y !== undefined)

              if (existingRoundSeeds.length) {
                const minY = Math.min(...existingRoundSeeds)
                const maxY = Math.max(...existingRoundSeeds)
                const midY = (minY + maxY) / 2
                y = midY - bracketHeight / 2
              } else {
                y = (totalHeight - bracketHeight) / 2
              }
            }
          } else if (depYs.length === 1) {
            y = depYs[0]
          } else {
            const centers = deps
              .map((d) => findPlaced(d.sourceBracketId)?.y)
              .filter((v): v is number => v !== undefined)
            if (centers.length) {
              const minY = Math.min(...centers)
              const maxY = Math.max(...centers)
              const midY = (minY + maxY) / 2
              y = midY - bracketHeight / 2
            } else {
              y = depYs[0]
            }
          }

          if (Number.isNaN(y)) {
            y = 0
          }

          finals.push({
            id: seed.id,
            roundIndex: maxRoundCount + fi,
            x: baseX + fi * roundSpacing,
            y,
            seed,
            region: 'final',
          })
        }
      }
    }
  }

  positioned.push(...finals)

  if (isSingle && data?.thirdPlace?.length) {
    const anchorBracket =
      finals[0] ||
      positioned
        .filter((p) => p.region === 'upper')
        .reduce(
          (acc, cur) => (!acc || cur.x > acc.x ? cur : acc),
          undefined as PositionedBracket | undefined
        )

    const baseX = anchorBracket
      ? anchorBracket.x
      : startX + data.upper.length * roundSpacing
    let baseY =
      (anchorBracket ? anchorBracket.y + bracketHeight : upperLayout.height) +
      thirdPlaceGap

    for (let rIndex = 0; rIndex < data.thirdPlace.length; rIndex++) {
      const round = data.thirdPlace[rIndex]
      for (let si = 0; si < round.seeds.length; si++) {
        const seed = round.seeds[si]
        positioned.push({
          id: seed.id,
          roundIndex:
            (anchorBracket?.roundIndex || data.upper.length) + 1 + rIndex,
          x: baseX + rIndex * 0,
          y: baseY + si * (bracketHeight + leafGap),
          seed,
          region: 'third',
        })
      }
      baseY += round.seeds.length * (bracketHeight + leafGap) + thirdPlaceGap
    }
  }

  const width =
    Math.max(...positioned.map((b) => b.x)) + bracketWidth + roundSpacing * 0.5
  const height =
    Math.max(...positioned.map((b) => b.y)) + bracketHeight + leafGap * 2

  return {
    all: positioned,
    bracketWidth,
    bracketHeight,
    width,
    height,
  }
}
